import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import format from 'string-template';
import CopyAllIcon from '@mui/icons-material/CopyAll';

import {
  Card,
  Container,
  HeaderContainer,
  SubTitle,
  Title,
  TitleContainer,
} from '../../../../../components/v2/Card';
import { StyledTextField, StyledTextFieldContainer } from '../../../../../components/Inputs';
import InputLabel from '../../../../../components/v2/InputLabel';
import SaveButton from '../../../../../components/Button/ActionButtons/SaveButton';
import PrimaryButton from '../../../../../components/Button/ActionButtons/PrimaryButton';
import { useInstagramDMEnabled } from '../../../../../utils/postHog';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import InstagramUser from '../../../../../Model/InstagramUser';
import RadioButtonGroup from '../../../../components/RadioButtonGroup';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useToast from '../../../../hooks/useToast';
import CoachConfig, {
  AutomatedTextSetterOptions,
  SetterTouchPoint,
  DEFAULT_SETTER_SEND_PERCENTAGE,
  MAX_SETTER_SEND_PERCENTAGE,
} from '../../../../Model/CoachConfig';
import {
  PlaceholderButton,
  PlaceholdersContainer,
  PlaceholderTip,
} from '../../../../components/PlaceHolder/styles';
import { SettingOption } from '../../utils';

import {
  IntegrationIssue,
  LEAD_NAME_PLACEHOLDER,
  LEAD_NAME_PLACEHOLDER_KEY,
  getIntegrationIssue,
  validateTemplate,
} from './utils';
import {
  PercentageFieldContainer,
  StyledAlert,
  StyledHelperText,
  TemplateFieldContainer,
  TemplateRow,
} from './styles';
import texts from './texts.json';

const messagePlaceholders = [LEAD_NAME_PLACEHOLDER_KEY];

/**
 * The touchpoint this card configures. The first message is the only automated touchpoint in
 * scope; the pre-call and post-call touchpoints get their own settings under their own keys once
 * each is validated, so this card is scoped rather than assuming a single set of setter settings.
 */
const TOUCH_POINT = SetterTouchPoint.FIRST_MESSAGE;

const radioButtonOptions = [
  { label: texts.enabled, value: SettingOption.ENABLED },
  { label: texts.disabled, value: SettingOption.DISABLED },
];

const integrationIssueTexts = {
  [IntegrationIssue.NOT_CONNECTED]: texts.integrationNotConnected,
  [IntegrationIssue.SESSION_EXPIRED]: texts.integrationSessionExpired,
  [IntegrationIssue.MISSING_MESSAGE_PERMISSION]: texts.integrationMissingPermission,
};

const AutomatedTextSetter = ({ coachId }) => {
  const [coachConfigDoc, setCoachConfigDoc] = useState(null);
  const [instagramUserDoc, setInstagramUserDoc] = useState(null);

  const [isCircuitBroken, setIsCircuitBroken] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [sendPercentage, setSendPercentage] = useState(`${DEFAULT_SETTER_SEND_PERCENTAGE}`);
  const [messageTemplate, setMessageTemplate] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const { userDoc } = useContext(LoggedInUserContext);

  const isInstagramDMEnabled = useInstagramDMEnabled();

  // Undefined while the flag is still resolving, which reads as read-only until it lands.
  const isReadOnly = !isInstagramDMEnabled;

  const sendPercentageNumber = Number(sendPercentage);
  const isSendPercentageValid = sendPercentage !== ''
    && Number.isFinite(sendPercentageNumber)
    && sendPercentageNumber >= 0
    && sendPercentageNumber <= MAX_SETTER_SEND_PERCENTAGE;

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      setCoachConfigDoc(null);
      setInstagramUserDoc(null);
      if (!coachId) {
        return;
      }
      try {
        const [coachConfig, instagramUsers] = await Promise.all([
          CoachConfig.getCoachConfigByCoachId(coachId),
          InstagramUser.getSecondaryInstaUserByCoachId(coachId),
        ]);
        if (isComponentMountedRef.current) {
          setCoachConfigDoc(coachConfig);
          setInstagramUserDoc(instagramUsers?.docs[0] || null);
        }
      } catch (error) {
        showToast(format(texts.loadFailed, { error }), { error: true });
      } finally {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
        }
      }
    };
    init();
  }, [coachId, isComponentMountedRef, showToast]);

  useEffect(() => {
    if (!coachConfigDoc) {
      return;
    }
    setIsCircuitBroken(coachConfigDoc.isAutomatedTextSetterCircuitBroken);
    setIsEnabled(coachConfigDoc.isAutomatedTextSetterEnabled(TOUCH_POINT));
    setSendPercentage(`${coachConfigDoc.getAutomatedTextSetterSendPercentage(TOUCH_POINT)}`);
    setMessageTemplate(coachConfigDoc.getAutomatedTextSetterMessageTemplate(TOUCH_POINT));
  }, [coachConfigDoc]);

  const integrationWarning = useMemo(() => {
    if (isLoading) {
      return null;
    }
    return integrationIssueTexts[getIntegrationIssue(instagramUserDoc)] || null;
  }, [instagramUserDoc, isLoading]);

  const onSave = useCallback(async () => {
    if (!coachConfigDoc) {
      showToast(texts.configUnavailable, { error: true });
      return;
    }
    if (!isSendPercentageValid) {
      showToast(format(texts.sendPercentageInvalid, { max: MAX_SETTER_SEND_PERCENTAGE }), { error: true });
      return;
    }
    const { isEmpty, unknownPlaceholders } = validateTemplate(messageTemplate);
    if (isEmpty && isEnabled) {
      showToast(texts.templateRequired, { error: true });
      return;
    }
    if (unknownPlaceholders.length) {
      const message = unknownPlaceholders.length > 1
        ? texts.templateUnknownPlaceholders
        : texts.templateUnknownPlaceholder;
      showToast(format(message, {
        placeholders: unknownPlaceholders.join(', '),
        supported: LEAD_NAME_PLACEHOLDER,
      }), { error: true });
      return;
    }

    setIsSaving(true);
    try {
      await coachConfigDoc.updateAutomatedTextSetterConfig(TOUCH_POINT, {
        [AutomatedTextSetterOptions.IS_ENABLED]: isEnabled,
        [AutomatedTextSetterOptions.SEND_PERCENTAGE]: sendPercentageNumber,
        [AutomatedTextSetterOptions.MESSAGE_TEMPLATE]: messageTemplate.trim(),
      }, userDoc.name);
      showToast(texts.saved, { type: 'success' });
    } catch (error) {
      showToast(format(texts.error, { error }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [
    coachConfigDoc,
    isEnabled,
    isSendPercentageValid,
    sendPercentageNumber,
    messageTemplate,
    showToast,
    isComponentMountedRef,
    userDoc,
  ]);

  const onResetCircuitBreaker = useCallback(async () => {
    setIsSaving(true);
    try {
      await coachConfigDoc.resetAutomatedTextSetterCircuitBreaker(userDoc.name);
      if (isComponentMountedRef.current) {
        setIsCircuitBroken(false);
      }
      showToast(texts.circuitBreakerReset, { type: 'success' });
    } catch (error) {
      showToast(format(texts.error, { error }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [
    coachConfigDoc,
    showToast,
    isComponentMountedRef,
    userDoc,
  ]);

  const copyPlaceholderValue = useCallback(async (placeholderValue) => {
    await navigator.clipboard.writeText(`{${placeholderValue}}`);
    showToast(texts.placeholderCopied);
  }, [
    showToast,
  ]);

  return (
    <Card>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.description}</SubTitle>
        </TitleContainer>
      </HeaderContainer>
      <Container>
        {isCircuitBroken && (
          <StyledAlert
            severity="error"
            action={(
              <PrimaryButton
                onClick={onResetCircuitBreaker}
                disabled={isReadOnly}
                variant="danger"
                size="small"
                $fitToContent
              >
                {texts.circuitBrokenAction}
              </PrimaryButton>
            )}
          >
            {texts.circuitBrokenWarning}
          </StyledAlert>
        )}

        {!!integrationWarning && (
          <StyledAlert severity="warning">{integrationWarning}</StyledAlert>
        )}

        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.textSetter}</InputLabel>
          <RadioButtonGroup
            options={radioButtonOptions}
            selectedOption={isEnabled ? SettingOption.ENABLED : SettingOption.DISABLED}
            onOptionChange={(val) => setIsEnabled(val === SettingOption.ENABLED)}
            disabled={isReadOnly}
          />
        </StyledTextFieldContainer>

        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.sendPercentage}</InputLabel>
          <PercentageFieldContainer>
            <StyledTextField
              value={sendPercentage}
              onChange={(e) => setSendPercentage(e.target.value)}
              type="number"
              variant="outlined"
              title="%"
              disabled={isReadOnly}
              error={!isSendPercentageValid}
              InputProps={{ inputProps: { min: 0, max: MAX_SETTER_SEND_PERCENTAGE } }}
            />
          </PercentageFieldContainer>
          {!isSendPercentageValid && (
            <StyledHelperText $isError>
              {format(texts.sendPercentageInvalid, { max: MAX_SETTER_SEND_PERCENTAGE })}
            </StyledHelperText>
          )}
        </StyledTextFieldContainer>

        <StyledTextFieldContainer $withMargin>
          <InputLabel>{texts.messageTemplate}</InputLabel>
          <TemplateRow>
            <TemplateFieldContainer>
              <StyledTextField
                placeholder={texts.messageTemplatePlaceholder}
                value={messageTemplate}
                onChange={(e) => setMessageTemplate(e.target.value)}
                disabled={isReadOnly}
                multiline
                minRows={7}
              />
            </TemplateFieldContainer>
            <PlaceholdersContainer>
              <PlaceholderTip>{texts.placeholderTitle}</PlaceholderTip>
              {messagePlaceholders.map((placeholderValue) => (
                <PlaceholderButton
                  key={placeholderValue}
                  startIcon={<CopyAllIcon />}
                  onClick={() => copyPlaceholderValue(placeholderValue)}
                  disabled={isReadOnly}
                >
                  {`{${placeholderValue}}`}
                </PlaceholderButton>
              ))}
              <PlaceholderTip>{texts.placeholderDescription}</PlaceholderTip>
            </PlaceholdersContainer>
          </TemplateRow>
        </StyledTextFieldContainer>

        <SaveButton
          onClick={onSave}
          disabled={isReadOnly}
          $fitToContent
        >
          {texts.save}
        </SaveButton>
      </Container>
      <LoadingOverlay isLoading={isLoading || isSaving} />
    </Card>
  );
};

AutomatedTextSetter.propTypes = {
  coachId: PropTypes.string,
};

AutomatedTextSetter.defaultProps = {
  coachId: '',
};

export default compose(
  observer,
)(AutomatedTextSetter);
